/**
 * Hardware System card body — Tandem / Pullout / Hinge / Telescopic /
 * Sliding. Each hardware family builds its size-chip display from
 * different fields (Pullout: W:xxx D:xxx H:xxx, Tandem: DxH, Hinge:
 * "45° Crank", Telescopic: size_text, Sliding: size_text || size).
 *
 * Extracted from ProductsPage.js. Pure JSX.
 *
 * The `isValidDimension` sub-helper is a legacy hack: some records in
 * the DB have stray "mm" strings with no numeric prefix — displaying
 * those as chips looks broken. Filtering them here is safer than
 * silently deleting them in a data-heal script.
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus } from 'lucide-react';
import { formatINR } from '../../../lib/store';


// Only trust dimension strings that carry an actual number, not just
// "mm" (a common malformed record in the legacy hardware catalog).
function isValidDimension(val) {
  return val && val !== 'mm' && val.replace(/mm$/i, '').trim();
}


function buildVariantChipText(hsv, systemType) {
  if (systemType === 'pullout') {
    const parts = [];
    if (isValidDimension(hsv.width)) parts.push(`W:${hsv.width.replace(/mm$/i, '')}`);
    if (isValidDimension(hsv.depth)) parts.push(`D:${hsv.depth.replace(/mm$/i, '')}`);
    if (isValidDimension(hsv.height)) parts.push(`H:${hsv.height.replace(/mm$/i, '')}`);
    return parts.join(' ');
  }
  if (systemType === 'tandem') {
    return [hsv.depth, hsv.height].filter(isValidDimension).join('x');
  }
  if (systemType === 'hinge') {
    return hsv.crank ? `${hsv.crank} Crank` : '';
  }
  if (systemType === 'telescopic') {
    return isValidDimension(hsv.size_text) ? hsv.size_text : '';
  }
  if (systemType === 'sliding') {
    if (isValidDimension(hsv.size_text)) return hsv.size_text;
    return isValidDimension(hsv.size) ? hsv.size : '';
  }
  // Generic fallback: size_text > depth x height
  if (isValidDimension(hsv.size_text)) return hsv.size_text;
  return [hsv.depth, hsv.height].filter(isValidDimension).join(' x ');
}


export function HardwareSystemBlock({ group, idx, onSelect }) {
  const variants = group.hardware_system_variants || [];
  const previewCount = 4;
  const remaining = variants.length - previewCount;
  const minRate = Math.min(...variants.map(v => v.rate || 0)) || group.min_rate;

  return (
    <div className="space-y-2 flex-1 flex flex-col">
      {/* Size chips — up to 4, then "+N more" */}
      <div className="flex flex-wrap gap-1.5">
        {variants.slice(0, previewCount).map((hsv, hsvIdx) => {
          const text = (buildVariantChipText(hsv, group.hardware_system_type) || '').trim();
          if (!text) return null;
          return (
            <span
              key={hsvIdx}
              className="px-2 py-1 text-xs bg-heda-olive-tint text-heda-olive-dark rounded-lg border border-heda-olive/20"
            >
              {text}
            </span>
          );
        })}
        {remaining > 0 && (
          <span className="px-2 py-1 text-xs text-heda-mute">
            +{remaining} more
          </span>
        )}
      </div>

      {/* Starting-from price */}
      <div className="flex justify-between items-center text-sm mt-auto">
        <span className="text-heda-sub">From:</span>
        <span className="font-mono-data font-semibold text-heda-olive-dark">
          {formatINR(minRate)}/{group.rate_type?.replace('per ', '') || 'set'}
        </span>
      </div>

      <Button
        size="sm"
        className="rounded-xl w-full bg-heda-ink hover:bg-black text-white mt-2"
        onClick={() => onSelect(group)}
        data-testid={`hardware-system-add-btn-${idx}`}
      >
        <Plus className="w-4 h-4 mr-1" /> Select &amp; Add
      </Button>
    </div>
  );
}
